import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

export default function Financeiro() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [data, setData] = useState(null);
  const [agregado, setAgregado] = useState(null);
  const [pagando, setPagando] = useState(null);
  const [comprovante, setComprovante] = useState("");
  const [observacao, setObservacao] = useState("");
  const [filter, setFilter] = useState("pendente");

  const load = () => {
    api.get("/dashboard/financeiro").then(r => setData(r.data)).catch(e => { toast.error(formatApiError(e)); setData({ totais: { recebido: 0, montador: 0, montadora: 0, lucro: 0 }, registros: [] }); });
    if (isAdmin) api.get("/financeiro/agregado").then(r => setAgregado(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const marcarPago = async () => {
    try {
      const fd = new FormData();
      if (comprovante) fd.append("comprovante_url", comprovante);
      if (observacao) fd.append("observacao", observacao);
      await api.post(`/financeiro/${pagando.id}/marcar-pago`, fd);
      toast.success("Pagamento registrado. Histórico fica só no admin.");
      setPagando(null); setComprovante(""); setObservacao(""); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (!data) return <div className="p-12 text-muted-foreground">Carregando…</div>;

  const filtrados = data.registros.filter(r => filter === "todos" || r.status === filter);

  return (
    <div className="p-8">
      <div className="mb-8">
        <div className="label-cap">Financeiro</div>
        <h1 className="font-heading font-extrabold text-3xl tracking-tighter mt-1">Dashboard Financeiro</h1>
      </div>

      <div className="grid md:grid-cols-4 gap-px bg-border border border-border">
        <Card label="Recebido" v={data.totais.recebido} color="brand" />
        <Card label="Pago a montadores" v={data.totais.montador} />
        <Card label="Pago a montadoras" v={data.totais.montadora} />
        <Card label="Lucro" v={data.totais.lucro} highlight />
      </div>

      <Tabs defaultValue="registros" className="mt-10">
        <TabsList className="rounded-none">
          <TabsTrigger value="registros" data-testid="tab-fin-registros">Registros</TabsTrigger>
          {isAdmin && <TabsTrigger value="agregado" data-testid="tab-fin-agregado">Agregado por Cidade/Estado/Montador</TabsTrigger>}
        </TabsList>

        <TabsContent value="registros" className="mt-4">
          <div className="flex gap-2 mb-3">
            {["pendente", "pago", "todos"].map(f => (
              <button key={f} onClick={() => setFilter(f)}
                className={`btn-sharp px-4 py-1.5 text-xs uppercase tracking-wider ${filter === f ? "brand-bg text-white border-transparent" : "border border-border"}`}
                data-testid={`fin-filter-${f}`}>{f}</button>
            ))}
          </div>
          <div className="border border-border bg-white">
            <table className="w-full text-sm">
              <thead className="border-b border-border bg-[#F4F4F5]">
                <tr><Th>MontaJá</Th><Th>Montador</Th><Th>Produto</Th><Th>Tipo</Th><Th>Vl. Produto</Th><Th>Vl. Montador</Th><Th>Montadora</Th><Th>PIX Montador</Th><Th>Status</Th><Th></Th></tr>
              </thead>
              <tbody>
                {filtrados.length === 0 && <tr><td colSpan={10} className="p-8 text-center text-muted-foreground">Nenhum registro.</td></tr>}
                {filtrados.map((r) => (
                  <tr key={r.id} className="border-b border-border last:border-0" data-testid={`fin-row-${r.id}`}>
                    <Td className="font-mono text-xs">
                      <a href={`/admin/servicos/${r.servico_id}`} className="inline-flex items-center gap-1 text-[#142766] hover:underline font-bold"
                        title="Abrir detalhe da OS" data-testid={`fin-os-link-${r.id}`}>
                        {r.pim_code} ↗
                      </a>
                    </Td>
                    <Td className="text-xs">{r.montador_nome || "—"}</Td>
                    <Td className="text-xs max-w-[180px] truncate" title={r.produto}>{r.produto || "—"}</Td>
                    <Td>{r.tipo}</Td>
                    <Td className="font-mono">R$ {Number(r.valor_produto || r.v_recebido || 0).toFixed(2)}</Td>
                    <Td className="font-mono">R$ {r.v_montador?.toFixed(2)}</Td>
                    <Td className="font-mono">R$ {r.v_montadora?.toFixed(2)}</Td>
                    <Td className="font-mono text-xs max-w-[150px] truncate" title={r.montador_pix}>{r.montador_pix || "—"}</Td>
                    <Td>
                      <span className={`inline-block px-2 py-1 text-xs uppercase tracking-wider ${r.status === "pago" ? "bg-[#00A859] text-white" : "bg-[#FFCC00] text-foreground"}`}>
                        {r.status}
                      </span>
                    </Td>
                    <Td>
                      {r.status === "pendente" && (
                        <Button size="sm" className="btn-sharp brand-bg text-white h-8 text-xs"
                          onClick={() => setPagando(r)} data-testid={`fin-pagar-${r.id}`}>
                          Marcar pago
                        </Button>
                      )}
                    </Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="agregado" className="mt-4">
          {agregado && (
            <div className="grid md:grid-cols-2 gap-6">
              <Bloco titulo="Lucro por cidade" itens={agregado.por_cidade} />
              <Bloco titulo="Lucro por estado" itens={agregado.por_estado} />
              <Bloco titulo="Pago por montador" itens={agregado.por_montador} />
              <Bloco titulo="Lucro por empresa" itens={agregado.por_empresa} />
            </div>
          )}
        </TabsContent>
      </Tabs>

      <Dialog open={!!pagando} onOpenChange={(o) => !o && setPagando(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Marcar como pago — {pagando?.pim_code}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="text-sm space-y-1 border border-border p-3 bg-[#F4F4F5]">
              <div>Montador: <strong>R$ {pagando?.v_montador?.toFixed(2)}</strong></div>
              <div>Período: {pagando?.periodo}</div>
            </div>
            <div>
              <Label className="label-cap">URL do comprovante (opcional)</Label>
              <Input value={comprovante} onChange={(e) => setComprovante(e.target.value)} className="mt-2" data-testid="fin-comprovante" />
            </div>
            <div>
              <Label className="label-cap">Observação</Label>
              <Textarea value={observacao} onChange={(e) => setObservacao(e.target.value)} className="mt-2" data-testid="fin-observacao" />
            </div>
            <div className="text-xs text-muted-foreground">
              Após confirmar, o lançamento sai do painel do montador e fica arquivado apenas no admin.
            </div>
            <Button onClick={marcarPago} className="w-full btn-sharp brand-bg text-white" data-testid="fin-confirmar-pago">Confirmar pagamento</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Card({ label, v, color, highlight }) {
  return (
    <div className={`bg-white p-5 ${highlight ? "border-l-4 border-l-[#00A859]" : ""}`}>
      <div className="label-cap">{label}</div>
      <div className={`font-heading font-extrabold text-3xl mt-2 font-mono ${color === "brand" ? "brand-text" : ""}`}>
        R$ {Number(v).toLocaleString("pt-BR", { minimumFractionDigits: 2 })}
      </div>
    </div>
  );
}
function Bloco({ titulo, itens }) {
  const entries = Object.entries(itens || {}).sort((a, b) => b[1] - a[1]);
  return (
    <div className="border border-border bg-white p-5">
      <div className="label-cap mb-3">{titulo}</div>
      {entries.length === 0 && <div className="text-sm text-muted-foreground">Sem dados.</div>}
      {entries.map(([k, v]) => (
        <div key={k} className="flex justify-between py-2 border-b border-border last:border-0 text-sm">
          <span className="truncate">{k}</span>
          <span className="font-mono font-bold">R$ {Number(v).toFixed(2)}</span>
        </div>
      ))}
    </div>
  );
}
function Th({ children }) { return <th className="text-left p-3 label-cap font-medium">{children}</th>; }
function Td({ children, className="" }) { return <td className={`p-3 ${className}`}>{children}</td>; }
